import { IContextualMenuItemProps, IContextualMenuItemRenderFunctions, IIconProps, makeStyles } from '@fluentui/react';
import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { DevBoxFailureReason } from '../../../constants/dev-box';
import { FeatureFlagName } from '../../../constants/features';
import { Metric, Property, Severity } from '../../../constants/telemetry';
import devBoxCardPreviewWin11Image from '../../../content/images/dev-box-card-preview-win11.jpg';
import { useActionCreator } from '../../../hooks/action-creator';
import { CustomizationGroupStatus } from '../../../models/customization';
import { HibernateSupport } from '../../../models/pool';
import { dismissContent as dismissContentActionCreator } from '../../../redux/actions/application/application-action-creators';
import {
    getRemoteAppOptionsSelector,
    getUseMultiMonitorSelector,
} from '../../../redux/selector/user-settings-selector';
import { DevBoxState } from '../../../redux/store/dev-box-state';
import { RemoteAppOption } from '../../../types/user-settings';
import { openInNewWindow, openInSameWindow, operatingSystemFamily } from '../../../utilities/browser';
import {
    getOverallCustomizationGroupStatus,
    isFailedCustomizationGroupStatus,
} from '../../../utilities/customization-group';
import { isFeatureFlagEnabled } from '../../../utilities/features';
import { isNotUndefinedOrWhiteSpace } from '../../../utilities/string';
import { trackMetric, trackTrace } from '../../../utilities/telemetry/channel';
import { getTimeStringFromDate } from '../../../utilities/time';
import { tryAddMultiMonitorAndSourceParameters } from '../../../utilities/url';
import { ConfirmationDialogProperties } from '../../common/confirmation-dialog/contexts';
import { FluentIconNames } from '../../common/fluent-icon-names';
import { MetadataItemViewModel } from '../../common/metadata/models';
import { PreviewTag } from '../../common/tag/preview-tag';
import { CustomizationDetailsPanelContextProperties } from '../../customization-details-panel/contexts';
import { ErrorDetailsPanelContextProperties } from '../../error-details-panel/contexts';
import ResourceCard from '../../resource-card/resource-card';
import { getResourceCardAction } from '../../resource-card/selectors';
import { OpenConnectViaAppDialogProperties } from '../../user-settings/connect-via-app-dialog/contexts';
import { DelayShutdownDialogProperties } from '../delay-shutdown-dialog/contexts';
import { DevBoxDetailsPanelContextProperties } from '../dev-box-details-panel/context';
import { OSTypeMetadata, shimmeredDevBoxMetadata } from '../dev-box-metadata/dev-box-metadata';
import { DevBoxSupportPanelContextProperties } from '../dev-box-support-panel/context';
import { HibernatePreviewDialogProperties } from '../hibernate-preview-dialog/context';
import { DevBoxViewModel } from '../models';
import { OpenWindowsDesktopClientDialogProperties } from '../open-windows-desktop-client-dialog/contexts';
import { RepairDialogProperties } from '../repair-dialog/context';
import DevBoxStateMessage from './dev-box-state-message';
import DevBoxStatusImage from './dev-box-status-image';
import { RepairOperationResultOutcome } from './models';
import {
    getAreActionsDisabled,
    getAriaLabelMessageDescriptorForOSType,
    getDevBoxCardBannerViewModel,
    getDevBoxCardMetadata,
    getIconForOSType,
    getPrimaryActions,
    getRepairDismissableContentId,
    getResourceCardError,
    getScheduleShutdownMetadataViewModel,
    getSecondaryActions,
    getUseTranslucentPreviewImage,
} from './selectors';

interface DevBoxCardProps {
    devBox: DevBoxViewModel;
    locale: string;
    onDeleteSubmitted: (resourceId: string) => void;
    onGetRemoteConnectionSubmitted: (resourceId: string) => void;
    onOpenErrorPanel: (props: ErrorDetailsPanelContextProperties) => void;
    onOpenDevBoxDetailsPanel: (props: DevBoxDetailsPanelContextProperties) => void;
    onOpenDevBoxSupportPanel: (props: DevBoxSupportPanelContextProperties) => void;
    onStartSubmitted: (resourceId: string) => void;
    onDelayShutdownSubmitted: (devBoxId: string, delayUntil: Date) => void;
    onSkipShutdownSubmitted: (devBoxId: string) => void;
    onOpenDelayShutdownDialog: (properties: DelayShutdownDialogProperties) => void;
    onOpenOpenWindowsDesktopClientDialog: (properties: OpenWindowsDesktopClientDialogProperties) => void;
    onOpenConnectViaAppDialog: (properties: OpenConnectViaAppDialogProperties) => void;
    onOpenConfirmationDialog: (properties: ConfirmationDialogProperties) => void;
    onHibernateSubmitted: (resourceId: string) => void;
    onShutdownSubmitted: (resourceId: string) => void;
    onResumeSubmitted: (resourceId: string) => void;
    onRestartSubmitted: (resourceId: string) => void;
    onRepairSubmitted: (resourceId: string) => void;
    onOpenHibernatePreviewDialog: (props: HibernatePreviewDialogProperties) => void;
    onOpenRepairDialog: (props: RepairDialogProperties) => void;
    onOpenCustomizationPanel: (props: CustomizationDetailsPanelContextProperties) => void;
    isHibernatePreviewDialogDismissed: boolean;
}

const messages = defineMessages({
    deleteDialogPrimaryButtonAriaLabel: {
        id: 'DevBoxCard_DeleteDialogPrimaryButton_AriaLabel',
        defaultMessage: 'Delete the dev box',
        description: 'Aria label for the submit button to confirm deleting the dev box',
    },
    deleteDialogPrimaryButtonText: {
        id: 'DevBoxCard_DeleteDialogPrimaryButton_Text',
        defaultMessage: 'Delete',
        description: 'Confirmation button to delete the dev box',
    },
    deleteDialogSubText: {
        id: 'DevBoxCard_DeleteDialogSubText_Text',
        defaultMessage:
            'This workstation and all its data will be permanently removed. Are you sure you want to delete {name}?',
        description:
            'Informs the user about the effects of deleting the dev box and confirms. {name} should not be localized, it is the name of the dev box.',
    },
    deleteDialogTitle: {
        id: 'DevBoxCard_DeleteDialogTitle_Text',
        defaultMessage: 'Delete dev box',
        description: 'Title text for "Delete dev box" dialog',
    },
    deleteMenuItemAriaLabel: {
        id: 'DevBoxCard_DeleteMenuItem_AriaLabel',
        defaultMessage: 'Delete dev box',
        description: 'Aria label for "Delete" contextual menu item in dev box card\'s settings button.',
    },
    deleteMenuItemText: {
        id: 'DevBoxCard_DeleteMenuItem_Text',
        defaultMessage: 'Delete',
        description: 'Text for "Delete" contextual menu item in dev box card\'s settings button.',
    },
    delayShutdownMenuItemAriaLabel: {
        id: 'DevBoxCard_DelayMenuItem_AriaLabel',
        defaultMessage: 'Delay shutdown for dev box',
        description: 'Aria label for "Delay shutdown" contextual menu item in dev box card\'s settings button.',
    },
    delayMenuItemText: {
        id: 'DevBoxCard_DelayMenuItem_Text',
        defaultMessage: 'Delay shutdown',
        description: 'Text for "Delay shutdown" contextual menu item in dev box card\'s settings button.',
    },
    delayShutdownMenuItemText: {
        id: 'DevBoxCard_DelayShutdownMenuItem_Text',
        defaultMessage: 'Delay scheduled shutdown',
        description: 'Text for "Delay scheduled shutdown" contextual menu item in dev box card\'s settings button.',
    },
    delayHibernateMenuItemText: {
        id: 'DevBoxCard_DelayHibernateMenuItem_Text',
        defaultMessage: 'Delay scheduled hibernate',
        description: 'Text for "Delay scheduled hibernate" contextual menu item in dev box card\'s settings button.',
    },
    openInBrowserMenuItemAriaLabel: {
        id: 'DevBoxCard_OpenInBrowserMenuItem_AriaLabel',
        defaultMessage: 'Open in browser',
        description: 'Aria label for "Open in Browser" button in split button.',
    },
    openInBrowserMenuItemText: {
        id: 'DevBoxCard_OpenInBrowserMenuItem_Text',
        defaultMessage: 'Open in browser',
        description: 'Text for "Open in Browser" button in dev box card.',
    },
    connectViaAppMenuItemAriaLabel: {
        id: 'DevBoxCard_ConnectViaAppMenuItem_AriaLabel',
        defaultMessage: 'Connect via app',
        description: 'Aria label for "Connect via app" option in the open split button for dev box cards',
    },
    connectViaAppMenuItemText: {
        id: 'DevBoxCard_ConnectViaAppMenuItem_Text',
        defaultMessage: 'Connect via app',
        description: 'Text for "Connect via app" option in the open split button for dev box cards',
    },
    startItemAriaLabel: {
        id: 'DevBoxCard_StartMenuItem_AriaLabel',
        defaultMessage: 'Start dev box',
        description: 'Aria label for "Start" contextual menu item in dev box card\'s settings button.',
    },
    startItemText: {
        id: 'DevBoxCard_StartMenuItem_Text',
        defaultMessage: 'Start',
        description: 'Text for "Start" contextual menu item in dev box card\'s settings button.',
    },
    stopItemAriaLabel: {
        id: 'DevBoxCard_StopMenuItem_AriaLabel',
        defaultMessage: 'Stop dev box',
        description: 'Aria label for "Stop" contextual menu item in dev box card\'s settings button.',
    },
    stopItemText: {
        id: 'DevBoxCard_StopMenuItem_Text',
        defaultMessage: 'Stop',
        description: 'Text for "Stop" contextual menu item in dev box card\'s settings button.',
    },
    devBoxDetailsItemAriaLabel: {
        id: 'DevBoxCard_DevBoxDetailsMenuItem_AriaLabel',
        defaultMessage: 'More info',
        description: 'Aria label for "More Info" contextual menu item in dev box card\'s settings button.',
    },
    devBoxDetailsItemText: {
        id: 'DevBoxCard_DevBoxDetailsStopMenuItem_Text',
        defaultMessage: 'More Info',
        description: 'Text for "More Info" contextual menu item in dev box card\'s settings button.',
    },
    devBoxSupportItemAriaLabel: {
        id: 'DevBoxCard_DevBoxSupportMenuItem_AriaLabel',
        defaultMessage: 'Support',
        description: 'Aria label for "Support" contextual menu item in dev box card\'s settings button.',
    },
    devBoxSupportItemText: {
        id: 'DevBoxCard_DevBoxSupportMenuItem_Text',
        defaultMessage: 'Support',
        description: 'Text for "Support" contextual menu item in dev box card\'s settings button.',
    },
    hibernateMenuItemText: {
        id: 'DevBoxCard_HibernateMenuItem_Text',
        defaultMessage: 'Hibernate',
        description: 'Text for "Hibernate" contextual menu item in dev box card\'s settings button.',
    },
    resumeMenuItemText: {
        id: 'DevBoxCard_ResumeMenuItem_Text',
        defaultMessage: 'Resume',
        description: 'Text for "Resume" contextual menu item in dev box card\'s settings button.',
    },
    shutdownMenuItemText: {
        id: 'DevBoxCard_ShutdownMenuItem_Text',
        defaultMessage: 'Shut down',
        description: 'Text for "Shut down" contextual menu item in dev box card\'s settings button.',
    },
    restartItemAriaLabel: {
        id: 'DevBoxCard_RestartMenuItem_AriaLabel',
        defaultMessage: 'Restart dev box',
        description: 'Aria label for "Restart" contextual menu item in dev box card\'s settings button.',
    },
    restartItemText: {
        id: 'DevBoxCard_RestartMenuItem_Text',
        defaultMessage: 'Restart',
        description: 'Text for "Restart" contextual menu item in dev box card\'s settings button',
    },
    repairItemAriaLabel: {
        id: 'DevBoxCard_RepairMenuItem_AriaLabel',
        defaultMessage: 'Troubleshoot & repair dev box',
        description: 'Aria label for "Troubleshoot & repair" contextual menu item in dev box card\'s settings button.',
    },
    repairItemText: {
        id: 'DevBoxCard_RepairMenuItem_Text',
        defaultMessage: 'Troubleshoot & repair',
        description: 'Text for "Troubleshoot & repair" contextual menu item in dev box card\'s settings button',
    },
    spinnerAriaLabel: {
        id: 'DevBoxCard_Spinner_AriaLabel',
        defaultMessage: 'In progress operation on your dev box',
        description: 'Aria label for spinner tracking progress of dev box operation in dev box card',
    },
    customizationsItemAriaLabel: {
        id: 'DevBoxCard_CustomizationsMenuItem_AriaLabel',
        defaultMessage: 'Customizations',
        description: 'Aria label for "Customizations" contextual menu item in dev box card\'s settings button.',
    },
    customizationsItemText: {
        id: 'DevBoxCard_CustomizationsMenuItem_Text',
        defaultMessage: 'Customizations',
        description: 'Text for "Customizations" contextual menu item in dev box card\'s settings button',
    },
    // TODO: Remove this message after removing the feature flag for Connect via app
    openInRdpClientMenuItemAriaLabel: {
        id: 'DevBoxCard_OpenInRdpClientMenuItem_AriaLabel',
        defaultMessage: 'Open in RDP Client',
        description: 'Aria label for "Open in RDP Client" option in the open split button for dev box cards',
    },
    openInRdpClientMenuItemText: {
        id: 'DevBoxCard_OpenInRdpClientMenuItem_Text',
        defaultMessage: 'Open in RDP Client',
        description: 'Text for "Open in RDP Client" option in the open split button for dev box cards',
    },
});

/**
 * Style Section
 */

const useHibernateMenuItemTextStyles = makeStyles(() => ({
    textContainer: {
        flexGrow: 0,
    },
}));

/**
 * END Styles
 */

export const DevBoxCard: React.FC<DevBoxCardProps> = React.memo((props: DevBoxCardProps) => {
    const {
        devBox,
        locale,
        onDeleteSubmitted,
        onGetRemoteConnectionSubmitted,
        onOpenErrorPanel,
        onOpenDevBoxDetailsPanel,
        onStartSubmitted,
        onDelayShutdownSubmitted,
        onSkipShutdownSubmitted,
        onOpenDelayShutdownDialog,
        onOpenOpenWindowsDesktopClientDialog,
        onOpenConnectViaAppDialog,
        onOpenConfirmationDialog,
        onHibernateSubmitted,
        onShutdownSubmitted,
        onResumeSubmitted,
        onRestartSubmitted,
        onRepairSubmitted,
        onOpenHibernatePreviewDialog,
        onOpenRepairDialog,
        onOpenCustomizationPanel,
        onOpenDevBoxSupportPanel,
        isHibernatePreviewDialogDismissed,
    } = props;

    const {
        remoteDesktopUrl,
        cloudPcConnectionUrl,
        resource,
        state,
        webUrl,
        isCardContentReady,
        devBoxActionState,
        customizationGroup,
        customizationGroups,
        repairOperation,
        failureOnDevBox,
        customizationFailure,
        pool,
        projectDisplayName,
    } = devBox;

    const { result: repairOperationResult } = repairOperation ?? {};

    const customizationGroupStatus = React.useMemo(
        () => getOverallCustomizationGroupStatus(customizationGroups),
        [customizationGroups, getOverallCustomizationGroupStatus]
    );

    const userRemoteAppOption = useSelector(getRemoteAppOptionsSelector);
    const userUseMultiMonitor = useSelector(getUseMultiMonitorSelector);

    const {
        name,
        projectName: projectResourceName,
        poolName,
        location,
        createdTime,
        osType,
        imageReference,
        hardwareProfile,
        storageProfile,
        hibernateSupport,
        uri,
        uniqueId,
    } = resource;

    const { originalScheduledStopTime, nextScheduledStopTime, skipState, delayState } = devBoxActionState;

    // Intl hooks
    const { formatMessage } = useIntl();

    // Style hooks
    const hibernateMenuItemTextStyles = useHibernateMenuItemTextStyles();

    // Note: putting these memoizers here since they're used within callbacks
    const hasHibernateSupport = hibernateSupport === HibernateSupport.Enabled;

    const resourceCardError = React.useMemo(
        () =>
            getResourceCardError(
                failureOnDevBox,
                state,
                hasHibernateSupport,
                devBoxActionState,
                repairOperationResult,
                customizationGroupStatus,
                customizationFailure
            ),
        [
            failureOnDevBox,
            state,
            devBoxActionState,
            hasHibernateSupport,
            repairOperationResult,
            customizationGroupStatus,
            customizationFailure,
        ]
    );

    // If there are issues detected but could not be automatically fixed by repair operation, it is considered a failure
    const showSeeDetailsButton =
        !!resourceCardError?.failure ||
        repairOperationResult?.repairOutcome === RepairOperationResultOutcome.IssuesDetected ||
        isFailedCustomizationGroupStatus(customizationGroupStatus);

    const repairDismissableContentId = React.useMemo(
        () => getRepairDismissableContentId(uri, repairOperation?.operationId),
        [uri, repairOperation?.operationId]
    );

    const infoBannerIconProps: IIconProps = React.useMemo(
        () => ({
            iconName:
                state === DevBoxState.Repairing ||
                customizationGroupStatus === CustomizationGroupStatus.Running ||
                customizationGroupStatus === CustomizationGroupStatus.NotStarted
                    ? undefined
                    : FluentIconNames.Info,
        }),
        [state, customizationGroupStatus]
    );

    const scheduledStopTimeString = React.useMemo(
        () =>
            originalScheduledStopTime !== undefined
                ? getTimeStringFromDate(originalScheduledStopTime, locale)
                : undefined,
        [originalScheduledStopTime, locale]
    );

    const projectName = React.useMemo(
        () => (isNotUndefinedOrWhiteSpace(projectDisplayName) ? projectDisplayName : projectResourceName),
        [projectDisplayName, projectResourceName]
    );

    const isShimmered = !isCardContentReady;

    const dismissContent = useActionCreator(dismissContentActionCreator);

    // Callback hooks
    const onDismiss = React.useCallback(() => {
        if (repairDismissableContentId) {
            dismissContent({ content: repairDismissableContentId });
        }
    }, [repairDismissableContentId, dismissContent]);

    const onDeleteDialogPrimaryButtonClicked = React.useCallback(() => {
        onDeleteSubmitted(uri);
    }, [onDeleteSubmitted, uri]);

    const onDevBoxDetailsMenuItemClicked = React.useCallback(() => {
        const props: DevBoxDetailsPanelContextProperties = {
            name,
            createdOn: createdTime,
            poolName,
            location,
            imageReference,
            hardwareProfile,
            storageProfile,
            scheduledStopTime: scheduledStopTimeString,
            hibernateSupport: hibernateSupport ?? HibernateSupport.Disabled,
        };

        onOpenDevBoxDetailsPanel(props);
    }, [
        name,
        createdTime,
        poolName,
        location,
        imageReference,
        hardwareProfile,
        storageProfile,
        scheduledStopTimeString,
        onOpenDevBoxDetailsPanel,
        hibernateSupport,
    ]);

    const onDevBoxSupportMenuItemClicked = React.useCallback(() => {
        const props: DevBoxSupportPanelContextProperties = {
            devBoxUniqueID: uniqueId ?? '',
            poolName,
            poolDisplayName: pool?.displayName,
            devBoxName: name,
            poolId: pool?.uri,
        };
        onOpenDevBoxSupportPanel(props);
    }, [uniqueId, name, pool?.displayName, poolName, pool?.uri]);

    const onOpenInBrowserSubmitted = React.useCallback(() => {
        if (isNotUndefinedOrWhiteSpace(webUrl)) {
            openInNewWindow(webUrl);
        }
    }, [webUrl]);

    const onOpenInRdpClientSubmitted = React.useCallback(() => {
        const useWindowsApp = userRemoteAppOption === RemoteAppOption.useWindowsApp ?? false;
        const remoteDesktopConnection = useWindowsApp ? cloudPcConnectionUrl : remoteDesktopUrl;
        if (isNotUndefinedOrWhiteSpace(remoteDesktopConnection)) {
            /**
             * Update the remote connection uri formatting based on user settings.
             * We need to add the usemultimon parameter to the uri and set the source from devportal.
             */
            const updatedRemoteDesktopConnection = tryAddMultiMonitorAndSourceParameters(
                remoteDesktopConnection,
                userUseMultiMonitor
            );

            if (isNotUndefinedOrWhiteSpace(updatedRemoteDesktopConnection)) {
                if (isFeatureFlagEnabled(FeatureFlagName.ConnectViaApp)) {
                    if (userRemoteAppOption) {
                        openInSameWindow(updatedRemoteDesktopConnection);
                        trackMetric(Metric.DirectLaunchStarted, 1, {
                            properties: {
                                [Property.MultiMonitor]: userUseMultiMonitor,
                                [Property.PreferredRemoteApp]: userRemoteAppOption,
                            },
                        });
                    } else {
                        onOpenConnectViaAppDialog({ cloudPcConnectionUrl, remoteDesktopUrl });
                    }
                } else {
                    onOpenOpenWindowsDesktopClientDialog({ openInRdpClientUrl: updatedRemoteDesktopConnection });
                    trackMetric(Metric.DirectLaunchStarted, 1, {
                        properties: {
                            [Property.PreferredRemoteApp]: userRemoteAppOption,
                            [Property.MultiMonitor]: userUseMultiMonitor,
                        },
                    });
                }
            }
        }
    }, [
        onOpenOpenWindowsDesktopClientDialog,
        onOpenConnectViaAppDialog,
        remoteDesktopUrl,
        cloudPcConnectionUrl,
        userRemoteAppOption,
        userUseMultiMonitor,
    ]);

    const devBoxStateMessageComponent = <DevBoxStateMessage state={state} />;
    const devBoxStatusImageElement = <DevBoxStatusImage state={state} />;

    const onRetryFailedOperation = React.useCallback(() => {
        const { reason } = failureOnDevBox ?? {};

        if (!reason) {
            return;
        }

        switch (reason) {
            case DevBoxFailureReason.DeleteFailed:
                onDeleteSubmitted(uri);
                return;
            case DevBoxFailureReason.GetRemoteConnectionFailed:
                onGetRemoteConnectionSubmitted(uri);
                return;
            case DevBoxFailureReason.HibernateFailed:
                onHibernateSubmitted(uri);
                return;
            case DevBoxFailureReason.RepairFailed:
                onRepairSubmitted(uri);
                return;
            case DevBoxFailureReason.RestartFailed:
                onRestartSubmitted(uri);
                return;
            case DevBoxFailureReason.ResumeFailed:
                onResumeSubmitted(uri);
                return;
            case DevBoxFailureReason.ShutdownFailed:
                onShutdownSubmitted(uri);
                return;
            case DevBoxFailureReason.StartFailed:
                onStartSubmitted(uri);
                return;
            default:
                trackTrace(`Invalid state: retry not currently supported after failure reason "${reason}".`, {
                    severity: Severity.Warning,
                });
                return;
        }
    }, [
        failureOnDevBox?.reason,
        onDeleteSubmitted,
        onGetRemoteConnectionSubmitted,
        onStartSubmitted,
        onHibernateSubmitted,
        onShutdownSubmitted,
        uri,
    ]);

    const onSeeErrorDetailsClicked = React.useCallback(() => {
        if (!showSeeDetailsButton) {
            trackTrace('Invalid state: error details requested, but failure message is falsy or white space.', {
                severity: Severity.Warning,
            });
            return;
        }

        // Makes sure we open customization details panel on customization group failure
        if (isFailedCustomizationGroupStatus(customizationGroupStatus) && customizationGroups && customizationGroup) {
            onOpenCustomizationPanel({ customizationGroup, customizationGroups, devBoxName: name });
            return;
        }

        onOpenErrorPanel({ failure: resourceCardError?.failure, devBoxName: name, repairOperationResult });
    }, [
        resourceCardError,
        name,
        showSeeDetailsButton,
        customizationGroupStatus,
        customizationGroup,
        customizationGroups,
        onOpenErrorPanel,
        onOpenCustomizationPanel,
    ]);

    const onSeeCustomizationDetailsClicked = React.useCallback(() => {
        if (!customizationGroups || !customizationGroup) {
            trackTrace(
                'Unexpected state: tried to open customization group details, but no customizationGroup was defined.',
                { severity: Severity.Warning }
            );
            return;
        }

        onOpenCustomizationPanel({
            customizationGroup,
            customizationGroups,
            devBoxName: name,
        });
    }, [name, customizationGroup, customizationGroups, onOpenCustomizationPanel]);

    const onStartMenuItemClicked = React.useCallback(() => {
        onStartSubmitted(uri);
    }, [onStartSubmitted, uri]);

    const onShutdownMenuItemClicked = React.useCallback(() => {
        onShutdownSubmitted(uri);
    }, [onShutdownSubmitted, uri]);

    const onHibernateDialogSubmitted = React.useCallback(() => {
        onHibernateSubmitted(uri);
    }, [onHibernateSubmitted, uri]);

    const onHibernateMenuItemClicked = React.useCallback(() => {
        if (isHibernatePreviewDialogDismissed) {
            onHibernateSubmitted(uri);
        } else {
            onOpenHibernatePreviewDialog({ devBoxName: name, onHibernateSubmitted: onHibernateDialogSubmitted });
        }
    }, [
        onOpenHibernatePreviewDialog,
        onHibernateDialogSubmitted,
        name,
        onHibernateSubmitted,
        isHibernatePreviewDialogDismissed,
        uri,
    ]);

    const onResumeMenuItemClicked = React.useCallback(() => {
        onResumeSubmitted(uri);
    }, [onResumeSubmitted, uri]);

    const onRestartMenuItemClicked = React.useCallback(() => {
        onRestartSubmitted(uri);
    }, [onRestartSubmitted, uri]);

    const onRepairDialogSubmitted = React.useCallback(() => {
        onRepairSubmitted(uri);
    }, [onRepairSubmitted, uri]);

    const onRepairMenuItemClicked = React.useCallback(() => {
        onOpenRepairDialog({ onRepairSubmitted: onRepairDialogSubmitted });
    }, [onOpenRepairDialog, onRepairDialogSubmitted]);

    // We need both these values to calculate options for the skip / delay form
    const canDelayShutdown = !!nextScheduledStopTime && !!originalScheduledStopTime;

    const onDelayShutdownMenuItemClicked = React.useCallback(() => {
        if (canDelayShutdown) {
            onOpenDelayShutdownDialog({
                devBoxId: uri,
                locale,
                originalScheduledTime: originalScheduledStopTime,
                nextScheduledTime: nextScheduledStopTime,
                skipState,
                delayState,
                onDelaySubmitted: onDelayShutdownSubmitted,
                onSkipSubmitted: onSkipShutdownSubmitted,
                hasHibernateSupport,
            });
        }
    }, [
        onOpenDelayShutdownDialog,
        uri,
        locale,
        originalScheduledStopTime,
        nextScheduledStopTime,
        skipState,
        delayState,
        onDelayShutdownSubmitted,
        onSkipShutdownSubmitted,
        canDelayShutdown,
    ]);

    const onRenderHibernateMenuItemContent = React.useCallback(
        (props: IContextualMenuItemProps, defaultRenders: IContextualMenuItemRenderFunctions): React.ReactNode => (
            <>
                {defaultRenders.renderItemIcon(props)}
                {defaultRenders.renderItemName({ ...props, styles: hibernateMenuItemTextStyles })}
                <PreviewTag />
            </>
        ),
        [hibernateMenuItemTextStyles]
    );

    // Memoized data
    const arePrimaryActionsDisabled = React.useMemo(() => getAreActionsDisabled(state), [state]);
    const ariaLabelForOSType = React.useMemo(() => getAriaLabelMessageDescriptorForOSType(osType), [osType]);
    const deleteDialogSubTextValues = React.useMemo(() => ({ name }), [name]);
    const iconForOSType = React.useMemo(() => getIconForOSType(osType), [osType]);
    const useTranslucentPreviewImage = React.useMemo(() => getUseTranslucentPreviewImage(state), [state]);

    const onDeleteMenuItemClicked = React.useCallback(() => {
        onOpenConfirmationDialog({
            onPrimaryButtonSubmitted: onDeleteDialogPrimaryButtonClicked,
            primaryButtonAriaLabel: formatMessage(messages.deleteDialogPrimaryButtonAriaLabel),
            primaryButtonText: formatMessage(messages.deleteDialogPrimaryButtonText),
            subText: formatMessage(messages.deleteDialogSubText, deleteDialogSubTextValues),
            title: formatMessage(messages.deleteDialogTitle),
        });
    }, [onOpenConfirmationDialog, onDeleteDialogPrimaryButtonClicked, deleteDialogSubTextValues]);

    // Resource card actions
    const deleteMenuItem = React.useMemo(
        () =>
            getResourceCardAction(
                formatMessage(messages.deleteMenuItemAriaLabel),
                FluentIconNames.Delete,
                'delete',
                onDeleteMenuItemClicked,
                formatMessage(messages.deleteMenuItemText)
            ),
        [formatMessage, onDeleteMenuItemClicked]
    );

    const openInBrowserMenuItem = React.useMemo(
        () =>
            getResourceCardAction(
                formatMessage(messages.openInBrowserMenuItemAriaLabel),
                FluentIconNames.OpenInNewTab,
                'open-in-browser',
                onOpenInBrowserSubmitted,
                formatMessage(messages.openInBrowserMenuItemText)
            ),
        [formatMessage, onOpenInBrowserSubmitted]
    );

    const openInRdpClientMenuItem = React.useMemo(
        () =>
            isFeatureFlagEnabled(FeatureFlagName.ConnectViaApp)
                ? getResourceCardAction(
                      formatMessage(messages.connectViaAppMenuItemAriaLabel),
                      FluentIconNames.ConnectVirtualMachine,
                      'connect-via-app',
                      onOpenInRdpClientSubmitted,
                      formatMessage(messages.connectViaAppMenuItemText)
                  )
                : getResourceCardAction(
                      formatMessage(messages.openInRdpClientMenuItemAriaLabel),
                      FluentIconNames.OpenInNewTab,
                      'open-in-rdp-client',
                      onOpenInRdpClientSubmitted,
                      formatMessage(messages.openInRdpClientMenuItemText)
                  ),
        [formatMessage, onOpenInRdpClientSubmitted]
    );

    const startMenuItem = React.useMemo(
        () =>
            getResourceCardAction(
                formatMessage(messages.startItemAriaLabel),
                FluentIconNames.Play,
                'start',
                onStartMenuItemClicked,
                formatMessage(messages.startItemText)
            ),
        [formatMessage, onStartMenuItemClicked]
    );

    const shutdownMenuItem = React.useMemo(
        () =>
            getResourceCardAction(
                formatMessage(messages.shutdownMenuItemText),
                FluentIconNames.PowerButton,
                'shutdown',
                onShutdownMenuItemClicked,
                formatMessage(messages.shutdownMenuItemText)
            ),
        [formatMessage, onShutdownMenuItemClicked]
    );

    const delayShutdownMenuItem = React.useMemo(
        () =>
            getResourceCardAction(
                formatMessage(messages.delayShutdownMenuItemText),
                FluentIconNames.Next,
                'delay',
                onDelayShutdownMenuItemClicked,
                formatMessage(messages.delayShutdownMenuItemText)
            ),
        [formatMessage, onDelayShutdownMenuItemClicked]
    );

    const hibernateMenuItem = React.useMemo(
        () =>
            getResourceCardAction(
                formatMessage(messages.hibernateMenuItemText),
                FluentIconNames.Clock,
                'hibernate',
                onHibernateMenuItemClicked,
                formatMessage(messages.hibernateMenuItemText),
                onRenderHibernateMenuItemContent
            ),
        [formatMessage, onHibernateMenuItemClicked]
    );

    const resumeMenuItem = React.useMemo(
        () =>
            getResourceCardAction(
                formatMessage(messages.resumeMenuItemText),
                FluentIconNames.Play,
                'resume',
                onResumeMenuItemClicked,
                formatMessage(messages.resumeMenuItemText)
            ),
        [formatMessage, onResumeMenuItemClicked]
    );

    const restartMenuItem = React.useMemo(
        () =>
            getResourceCardAction(
                formatMessage(messages.restartItemAriaLabel),
                FluentIconNames.Refresh,
                'restart',
                onRestartMenuItemClicked,
                formatMessage(messages.restartItemText)
            ),
        [formatMessage, onRestartMenuItemClicked]
    );

    const devBoxDetailsMenuItem = React.useMemo(
        () =>
            getResourceCardAction(
                formatMessage(messages.devBoxDetailsItemAriaLabel),
                FluentIconNames.Info,
                'more-info',
                onDevBoxDetailsMenuItemClicked,
                formatMessage(messages.devBoxDetailsItemText)
            ),
        [formatMessage, onDevBoxDetailsMenuItemClicked]
    );

    const devBoxSupportMenuItem = React.useMemo(
        () =>
            getResourceCardAction(
                formatMessage(messages.devBoxSupportItemAriaLabel),
                FluentIconNames.Headset,
                'support',
                onDevBoxSupportMenuItemClicked,
                formatMessage(messages.devBoxSupportItemText)
            ),
        [formatMessage, onDevBoxSupportMenuItemClicked]
    );

    const repairMenuItem = React.useMemo(
        () =>
            getResourceCardAction(
                formatMessage(messages.repairItemAriaLabel),
                FluentIconNames.Repair,
                'repair',
                onRepairMenuItemClicked,
                formatMessage(messages.repairItemText)
            ),
        [formatMessage, onRepairMenuItemClicked]
    );

    const customizeMenuItem = React.useMemo(
        () =>
            getResourceCardAction(
                formatMessage(messages.customizationsItemAriaLabel),
                FluentIconNames.CheckList,
                'customization',
                onSeeCustomizationDetailsClicked,
                formatMessage(messages.customizationsItemText)
            ),
        [formatMessage, onSeeCustomizationDetailsClicked]
    );

    const primaryActions = React.useMemo(
        () => getPrimaryActions(openInBrowserMenuItem, openInRdpClientMenuItem, operatingSystemFamily, state),
        [openInBrowserMenuItem, openInRdpClientMenuItem, state]
    );

    const hasCustomizationGroup = !!customizationGroups;

    const secondaryActions = React.useMemo(
        () =>
            getSecondaryActions(
                deleteMenuItem,
                startMenuItem,
                state,
                devBoxDetailsMenuItem,
                canDelayShutdown,
                delayShutdownMenuItem,
                hasHibernateSupport,
                hibernateMenuItem,
                resumeMenuItem,
                shutdownMenuItem,
                restartMenuItem,
                repairMenuItem,
                customizeMenuItem,
                hasCustomizationGroup,
                devBoxSupportMenuItem
            ),
        [
            deleteMenuItem,
            startMenuItem,
            state,
            devBoxDetailsMenuItem,
            delayShutdownMenuItem,
            canDelayShutdown,
            hasHibernateSupport,
            hibernateMenuItem,
            resumeMenuItem,
            shutdownMenuItem,
            restartMenuItem,
            repairMenuItem,
            customizeMenuItem,
            hasCustomizationGroup,
            devBoxSupportMenuItem,
        ]
    );

    // Resource card metadata
    const osTypeMetadata: MetadataItemViewModel = React.useMemo(
        () => ({
            description: formatMessage(ariaLabelForOSType),
            icon: iconForOSType,
            key: 'os-type',
            primary: true,
            value: <OSTypeMetadata osType={osType} />,
        }),
        [ariaLabelForOSType, iconForOSType, osType, formatMessage]
    );

    const scheduleMetadata: MetadataItemViewModel | undefined = React.useMemo(
        () => getScheduleShutdownMetadataViewModel(locale, formatMessage, nextScheduledStopTime, hasHibernateSupport),
        [nextScheduledStopTime, locale, formatMessage, hasHibernateSupport]
    );

    const metadata = React.useMemo(
        () => getDevBoxCardMetadata(osTypeMetadata, state, scheduleMetadata),
        [osTypeMetadata, scheduleMetadata, state]
    );

    const cardBanner = React.useMemo(
        () =>
            getDevBoxCardBannerViewModel(
                devBox,
                locale,
                infoBannerIconProps,
                formatMessage(messages.spinnerAriaLabel),
                resourceCardError,
                onRetryFailedOperation,
                showSeeDetailsButton ? onSeeErrorDetailsClicked : undefined,
                customizationGroups ? onSeeCustomizationDetailsClicked : undefined,
                onDismiss
            ),
        [
            devBox,
            locale,
            infoBannerIconProps,
            formatMessage,
            onDismiss,
            resourceCardError,
            onRetryFailedOperation,
            onSeeErrorDetailsClicked,
            showSeeDetailsButton,
            customizationGroups,
            onSeeCustomizationDetailsClicked,
        ]
    );

    return (
        <ResourceCard
            arePrimaryActionsDisabled={arePrimaryActionsDisabled}
            metadata={metadata}
            name={name}
            stateElement={devBoxStateMessageComponent}
            statusImageElement={devBoxStatusImageElement}
            previewImageSrc={devBoxCardPreviewWin11Image}
            primaryActions={primaryActions}
            projectName={projectName}
            secondaryActions={secondaryActions}
            useTranslucentPreviewImage={useTranslucentPreviewImage}
            isShimmered={isShimmered}
            shimmeredMetadata={shimmeredDevBoxMetadata}
            cardBanner={cardBanner}
        />
    );
});

export default DevBoxCard;
